import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UserRequestAPIs from '../../../utilities/api/user/AdminUserRequestList';
import { useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RequestUserCard from './RequestUserCard';
import CustomPagination from '../../CustomPagination/CustomPagination';

const positionLabels = {
  0: 'TL student management body',
  1: 'Manager (other technical clubs)',
  2: 'TL Manager',
  3: 'TL Technician',
  4: 'Other',
};

const Request = ({ searchQuery }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [acceptedRows, setAcceptedRows] = useState([]);
  const [rejectedRows, setRejectedRows] = useState([]);

  const user = useSelector(state => state.user.user);

  const fetchAndFilterData = () => {
    UserRequestAPIs.UserRequestListGet()
      .then(result => {
        const filtered = result.filter(
          item => item.TL_name === user.user.TL_name
        );

        const formatted = filtered.map(item => ({
          id: item.id,
          email: item.email,
          name: item.name || item.username,
          profile_pic: item.profile_pic,
          TL: item.TL,
          TL_name: item.TL_name,
          phone_number: item.phone_number,
          position:
            item.other_position || positionLabels[item.position] || 'N/A',
          other_position: item.other_position,
          department: item.department,
          graduation_year: item.graduation_year,
          degree: item.degree,
          hostel_address: item.hostel_address,
          current_year: item.current_year,
          is_student: item.is_student,
          student_id: item.student_id,
        }));
        setTableData(formatted);
      })
      .catch(error => {
        toast.error(
          `Error fetching data: ${error.message || 'Unknown error occurred'}`
        );
        console.log('error', error);
      });
  };

  useEffect(() => {
    fetchAndFilterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleAccept = row => {
    setAcceptedRows([...acceptedRows, row.id]);
    setRejectedRows(rejectedRows.filter(id => id !== row.id));

    const formData = new FormData();
    formData.append('accepted', true);
    formData.append('email', row.email);

    UserRequestAPIs.AdminRequestAcceptDecline(formData)
      .then(() => {
        toast.success('Successfully accepted request!');
        fetchAndFilterData();
      })
      .catch(error => {
        toast.error(
          `Error accepting request for ${row.email}: ${
            error.message || 'Unknown error occurred'
          }`
        );
      });
  };

  const handleReject = row => {
    setRejectedRows([...rejectedRows, row.id]);

    const formData = new FormData();
    formData.append('accepted', false);
    formData.append('email', row.email);

    UserRequestAPIs.AdminRequestAcceptDecline(formData)
      .then(() => {
        toast.success('Declined request successfully!');
        fetchAndFilterData();
      })
      .catch(error => {
        toast.error(
          `Error declining request for ${row.email}: ${
            error.message || 'Unknown error occurred'
          }`
        );
      });
  };

  const isAccepted = row => acceptedRows.includes(row.id);
  const isRejected = row => rejectedRows.includes(row.id);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredData(tableData);
    } else {
      const lower = searchQuery.toLowerCase();
      setFilteredData(
        tableData.filter(
          row =>
            (row.name && row.name.toLowerCase().includes(lower)) ||
            (row.email && row.email.toLowerCase().includes(lower))
        )
      );
    }
  }, [searchQuery, tableData]);

  // =========================
  // Desktop Columns
  // =========================
  const desktopColumns = [
    {
      field: 'userName',
      headerName: 'User Name',
      flex: 2,
      minWidth: 150,
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={params.row.profile_pic || '/default-avatar.png'}
            sx={{ width: 40, height: 40 }}
          />
          <Box>
            <Typography fontWeight={600} fontSize="0.9rem">
              {params.row.name}
            </Typography>
            <Typography fontSize="0.75rem" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1.5,
      minWidth: 200,
      renderCell: params => (
        <Box>
          {params.row.other_position ||
            positionLabels[params.row.position] ||
            params.row.position}
        </Box>
      ),
    },
    {
      field: 'group',
      headerName: 'Group Name',
      flex: 1.3,
      minWidth: 150,
      renderCell: params => params.row.TL_name,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      renderCell: params => {
        const accepted = isAccepted(params.row);
        const rejected = isRejected(params.row);
        return (
          <Stack direction="row" spacing={1}>
            {!accepted && !rejected && (
              <Box display="flex" gap={3}>
                <Button
                  variant="text"
                  onClick={() => handleAccept(params.row)}
                  disabled={accepted}
                  sx={{
                    cursor: 'pointer',
                    color: '#128D16',
                  }}
                >
                  Accept
                </Button>

                <Button
                  variant="text"
                  onClick={() => handleReject(params.row)}
                  disabled={rejected}
                  sx={{
                    cursor: 'pointer',
                    color: '#D32F2F',
                  }}
                >
                  Decline
                </Button>
              </Box>
            )}

            {accepted && (
              <Box
                sx={{
                  border: '1.5px solid #128D16',
                  color: '#128D16',
                  bgcolor: '#E0FFE0',
                  px: '12px',
                  py: '8px',
                  borderRadius: 5,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Accepted <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />
              </Box>
            )}

            {rejected && (
              <Box
                sx={{
                  border: '1.5px solid #D32F2F',
                  color: '#D32F2F',
                  bgcolor: '#FFF0F0',
                  px: '12px',
                  py: '8px',
                  borderRadius: 5,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Declined <CloseIcon fontSize="small" sx={{ ml: 0.5 }} />
              </Box>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box width="100%">
      {isMobile ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr', // Mobile → 1 card
              sm: '1fr 1fr', // Tablet → 2 cards
            },
            gap: 2,
          }}
        >
          {filteredData.map(row => (
            <RequestUserCard
              key={row.id}
              row={row}
              onAccept={handleAccept}
              onReject={handleReject}
              isAccepted={isAccepted}
              isRejected={isRejected}
            />
          ))}
        </Box>
      ) : (
        
        <Box
          sx={{
            p: '12px 24px 24px 24px',
          }}
        >
          <DataGrid
            rows={filteredData}
            columns={desktopColumns}
            autoHeight
            disableColumnMenu
            disableRowSelectionOnClickpagination
            pageSizeOptions={[7, 10, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 7 } },
            }}
            paginationMode="client"
            slots={{
              pagination: CustomPagination,
            }}
            sx={{
              border: '1px solid #CCCCCC',
              borderRadius: '8px',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F2F2F2',
                borderRadius: '8px 8px 0px 0px',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#F3F4F6',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #F1F1F1',
              },
              '& .MuiDataGrid-footerContainer': {
                justifyContent: 'center',
                alignItems: 'center',
                '& .MuiTablePagination-root': {
                  textAlign: 'center',
                  '& .MuiButtonBase-root': {
                    color: '#3f3f3f',
                  },
                },
              },
              '& .MuiPaginationItem-root': {
                color: '#3f3f3f',
              },
              '& .MuiDataGrid-selectedRowCount': {
                visibility: 'hidden',
                width: 0,
                padding: 0,
                margin: 0,
                flex: '0 !important',
              },
              '& .MuiDataGrid-footerContainer': {
                paddingTop: '4px',
                paddingBottom: '4px',
                justifyContent: 'center',
                borderTop: '1px solid #F1F1F1',
              },
            }}
          />
        </Box>
      )}

      <ToastContainer />
    </Box>
  );
};

export default Request;
